/**
 * Scroll helpers for the widget message list.
 * Anchors the latest visitor question at the top of the viewport when
 * the bot replies, instead of scrolling the full thread to the bottom.
 */

const DEFAULT_PADDING_TOP_PX = 12

/**
 * @param {Array<{ from?: string, type?: string }>} messages
 * @returns {{ id?: string, from?: string } | null}
 */
export function findLastVisitorMessage(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (!msg || msg.type === 'typing_indicator') continue
    if (msg.from === 'visitor') return msg
  }
  return null
}

/**
 * @param {Array<{ from?: string, type?: string }>} messages
 * @returns {{ id?: string, from?: string } | null}
 */
export function findLastBotMessage(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (!msg || msg.type === 'typing_indicator') continue
    if (msg.from === 'chatbot') return msg
  }
  return null
}

/**
 * True when the thread has bot content but no visitor messages yet
 * (initial engagement / after "New Conversation").
 *
 * @param {Array<{ from?: string, type?: string }>} messages
 */
export function isBotOnlyThread(messages) {
  return findLastBotMessage(messages) != null && findLastVisitorMessage(messages) == null
}

/**
 * First bot row of the latest turn: the earliest consecutive chatbot
 * message after the most recent visitor message (or thread start).
 * BotMan intent replies arrive as several rows (text, then a tall list
 * carousel); the *text* row must be the scroll anchor, not the last row.
 *
 * @param {Array<{ id?: string, from?: string, type?: string }>} messages
 * @returns {{ id?: string, from?: string } | null}
 */
export function findTurnFirstBotMessage(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null
  let first = null
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (!msg || msg.type === 'typing_indicator') continue
    if (msg.from === 'visitor') break
    if (msg.from === 'chatbot') first = msg
  }
  return first
}

/**
 * True when the latest row is a bot message that was not preceded by a
 * visitor message in the same turn (whisper / intent / launch triggers).
 *
 * @param {Array<{ from?: string, type?: string }>} messages
 */
export function isProactiveBotTurn(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return false

  const last = messages[messages.length - 1]
  if (!last || last.type === 'typing_indicator') return false
  if (last.from !== 'chatbot') return false

  for (let i = messages.length - 2; i >= 0; i -= 1) {
    const msg = messages[i]
    if (!msg || msg.type === 'typing_indicator') continue
    return msg.from !== 'visitor'
  }

  return true
}

/**
 * @param {HTMLElement | null | undefined} containerEl
 * @param {{ behavior?: ScrollBehavior }} [options]
 */
export function scrollContainerToTop(containerEl, options = {}) {
  if (!containerEl) return
  const { behavior = 'auto' } = options
  if (behavior === 'smooth' && typeof containerEl.scrollTo === 'function') {
    containerEl.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    containerEl.scrollTop = 0
  }
}

/**
 * @param {HTMLElement | null | undefined} containerEl
 * @returns {number}
 */
export function getMessageListPaddingTop(containerEl) {
  if (!containerEl || typeof getComputedStyle !== 'function') {
    return DEFAULT_PADDING_TOP_PX
  }
  const pt = parseFloat(getComputedStyle(containerEl).paddingTop)
  return Number.isFinite(pt) ? pt : DEFAULT_PADDING_TOP_PX
}

/**
 * @param {string} id
 * @returns {string}
 */
function messageIdSelector(id) {
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(String(id))
      : String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return `[data-message-id="${escaped}"]`
}

/**
 * @param {HTMLElement | null | undefined} containerEl
 * @param {string | undefined | null} messageId
 * @returns {HTMLElement | null}
 */
export function getMessageElement(containerEl, messageId) {
  if (!containerEl || messageId == null || messageId === '') return null
  return containerEl.querySelector(messageIdSelector(messageId))
}

/**
 * Scroll the message list so a row sits at the top (below padding).
 *
 * @param {HTMLElement | null | undefined} containerEl
 * @param {string | undefined | null} messageId
 * @param {{ behavior?: ScrollBehavior, paddingTop?: number, onFallback?: () => void }} [options]
 * @returns {boolean} true when anchored to the row
 */
export function scrollToMessageAnchor(containerEl, messageId, options = {}) {
  const { behavior = 'auto', paddingTop, onFallback } = options

  if (!containerEl || messageId == null || messageId === '') {
    onFallback?.()
    return false
  }

  const messageEl = getMessageElement(containerEl, messageId)
  if (!messageEl) {
    onFallback?.()
    return false
  }

  const pad = paddingTop ?? getMessageListPaddingTop(containerEl)
  // Prefer viewport-relative math so flex/gap layouts and nested
  // offsetParents still land the row at the top of the scrollport.
  let top
  if (typeof messageEl.getBoundingClientRect === 'function') {
    const containerRect = containerEl.getBoundingClientRect()
    const messageRect = messageEl.getBoundingClientRect()
    top = Math.max(
      0,
      containerEl.scrollTop + (messageRect.top - containerRect.top) - pad,
    )
  } else {
    top = Math.max(0, messageEl.offsetTop - pad)
  }

  if (behavior === 'smooth' && typeof containerEl.scrollTo === 'function') {
    containerEl.scrollTo({ top, behavior: 'smooth' })
  } else {
    containerEl.scrollTop = top
  }

  return true
}

/**
 * Scroll the message list so the visitor row sits at the top (below padding).
 *
 * @param {HTMLElement | null | undefined} containerEl
 * @param {string | undefined | null} visitorMessageId
 * @param {{ behavior?: ScrollBehavior, paddingTop?: number, onFallback?: () => void }} [options]
 * @returns {boolean} true when anchored to a visitor row
 */
export function scrollToVisitorAnchor(containerEl, visitorMessageId, options = {}) {
  return scrollToMessageAnchor(containerEl, visitorMessageId, options)
}
